import { FC, useState, useMemo } from 'react';
import { usePathContext } from '@new/common/hooks/usePathContext';
import { useGetLessonQuery } from '@teachur/http-client';
import { useLessonProgress } from '../hooks/useLessonProgress';
import { Can } from '@new/casl';

import { PageHeader } from '@new/common/ui/PageHeader/PageHeader';
import { EditButton } from '@new/common/ui/EditButton/EditButton';
import { Tabs, TabItem } from '@new/common/ui/Tabs/Tabs';
import { Progress } from '@new/common/ui/Progress/Progress';
import { LessonDetailTab } from '../components/LessonDetailTab/LessonDetailTab';
import { LessonExerciseTab } from '../components/LessonExerciseTab/LessonExerciseTab';
import { LessonReadingTab } from '../components/LessonReadingTab/LessonReadingTab';
import { NotesPanel } from '@new/features/notes/components/NotesPanel/NotesPanel';

type LessonTab = 'details' | 'exercise' | 'reading';

export const LessonDetailPage: FC = () => {
  const { lessonId = 0 } = usePathContext();

  const { data: lesson, isLoading } = useGetLessonQuery(lessonId);
  const progress = useLessonProgress(lesson?.objectives);
  const [selectedTab, setSelectedTab] = useState<LessonTab>('details');

  const tabs: TabItem<LessonTab>[] = useMemo(
    () => [
      {
        value: 'details',
        label: 'Lesson Detail',
        content: lesson ? <LessonDetailTab lesson={lesson} /> : null,
      },
      {
        value: 'exercise',
        label: 'Exercise',
        content: lesson ? <LessonExerciseTab lesson={lesson} /> : null,
      },
      {
        value: 'reading',
        label: 'Reading',
        content: lesson ? <LessonReadingTab lesson={lesson} /> : null,
      },
    ],
    [lesson]
  );

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p>Lesson not found</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full py-5 pt-0 px-7 flex flex-col gap-6">
      <PageHeader
        title={lesson.name}
        description={lesson.shortDescription}
        badge={{ text: 'Lesson', bgColor: 'var(--success-S300)', textColor: 'var(--success-S700)' }}
      />
      <Can I="update" a="Lesson">
        <EditButton />
      </Can>

      {/* Progress Bar */}
      <Can I="enroll" a="Degree">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB900)! font-semibold!">
              Lesson Progress
            </span>
            <span className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB900)! font-semibold!">
              {progress}%
            </span>
          </div>
          <Progress value={progress} className="w-full h-2.5" />
        </div>
      </Can>

      {/* Tabs */}
      <div className="flex flex-col gap-6">
        <Tabs tabs={tabs} value={selectedTab} onValueChange={setSelectedTab} />
      </div>
      <Can I="enroll" a="Degree">
        <NotesPanel relId={lessonId} rel="lesson" />
      </Can>
    </div>
  );
};
